import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Stack } from '@mui/material'
import Drawer from '@mui/material/Drawer'
import { updateTeamMember } from 'api/teams'
import DrawerContainer from 'components/drawer/common/DrawerContainer'
import DrawerHeader from 'components/drawer/common/DrawerHeader'
import {
  CaregiverRoleFields,
  combineRoleValues,
  splitRolesIntoCategories,
} from 'components/form-elements/CaregiverRoleFields'
import FormInput from 'components/form-elements/FormInput'
import { useAuthContext } from 'components/context/AuthProvider'
import NotificationSys from 'components/NotificationSystem'
import { defaultDrawerSX, EMAIL_IS_INVALID, minButtonWidth, PHONE_IS_INVALID } from 'const'
import { useCharactersLeft } from 'hooks/useCharactersLeft'
import React, { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { TeamMember, TeamMemberUpdateRequest, TraineeTeamRole } from 'types'
import { array, literal, object, string, TypeOf } from 'zod'
import { useTeams } from '../../hooks/useTeams'

const optional = string().optional()

export function TimelineTeamMemberDrawer({
  isOpen,
  onClose,
  teamMember,
}: {
  isOpen: boolean
  teamMember: TeamMember
  onClose: () => void
}) {
  const { activeTeamId, onRefetchTeamMembers, checkIsSystemManaged } = useTeams()
  const { isTrainee } = useAuthContext()

  const isReadOnly = isTrainee && checkIsSystemManaged(activeTeamId)

  const isRegisteredMember = useMemo(() => {
    return !!teamMember.user
  }, [teamMember])

  const formSchema = object({
    firstName: string(),
    lastName: string(),
    email: string().email(EMAIL_IS_INVALID).or(literal('')).optional(),
    phone: string().min(10, PHONE_IS_INVALID).or(literal('')).optional(),
    professionalRole: array(
      object({
        label: string(),
        value: string(),
      }),
    ).optional(),
    familyRole: array(
      object({
        label: string(),
        value: string(),
      }),
    ).optional(),
    otherRole: string().optional(),
    addressStreet: optional,
    addressZipCode: optional,
    addressCountry: optional,
    addressCity: optional,
    addressState: optional,
    comments: string(),
    about: string().max(200, 'Max message length is 200 characters').optional(),
  })

  type FormType = TypeOf<typeof formSchema>

  const defaultRoles = useMemo(() => {
    return splitRolesIntoCategories(teamMember.user?.role || teamMember.role)
  }, [teamMember])

  const formSettings = {
    mode: 'onChange' as const,
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: teamMember.user?.firstname || teamMember.firstName || '',
      lastName: teamMember.user?.lastname || teamMember.lastName || '',
      email: teamMember.user?.email || teamMember.email || '',
      professionalRole: defaultRoles.professionalRole,
      familyRole: defaultRoles.familyRole,
      otherRole: teamMember.user?.otherRole || teamMember.otherRole || '',
      addressStreet: teamMember.user?.addressStreet || teamMember.addressStreet || '',
      addressZipCode: teamMember.user?.addressZipCode || teamMember.addressZipCode || '',
      addressCountry: teamMember.user?.addressCountry || teamMember.addressCountry || '',
      addressCity: teamMember.user?.addressCity || teamMember.addressCity || '',
      addressState: teamMember.user?.addressState || teamMember.addressState || '',
      phone: teamMember.user?.phoneNumber || teamMember.phone || '',
      comments: teamMember.user?.comments || teamMember.comments || '',
      about: teamMember.user?.about || teamMember.about || '',
    },
  }

  const methods = useForm<FormType>(formSettings)

  const updateMutation = useMutation(updateTeamMember, {
    onSuccess: async () => {
      NotificationSys.showSuccess('Team Member successfully updated')
      onClose()
      await onRefetchTeamMembers()
    },
  })

  const onSubmitHandler = (values: FormType) => {
    if (!activeTeamId) {
      NotificationSys.showWarning('No active team selected')
      return
    }

    const request: TeamMemberUpdateRequest = {
      id: activeTeamId,
      memberId: teamMember.id,
      addressStreet: values.addressStreet || '',
      addressZipCode: values.addressZipCode || '',
      addressCountry: values.addressCountry || '',
      addressCity: values.addressCity || '',
      addressState: values.addressState || '',
      comments: values.comments || '',
    }

    // For unregistered members, send all fields
    if (!isRegisteredMember) {
      const role = combineRoleValues(values.professionalRole, values.familyRole)
      request.firstName = values.firstName
      request.lastName = values.lastName
      request.email = values.email
      request.phone = values.phone || ''
      request.role = role
      request.otherRole = role.includes(TraineeTeamRole.other) ? values.otherRole || '' : ''
      request.about = values.about || ''
    }

    updateMutation.mutate(request)
  }

  const aboutMeValue = methods.watch('about')
  const charactersLeft = useCharactersLeft(200, aboutMeValue?.length || 0)

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose} sx={defaultDrawerSX}>
      <DrawerContainer isOpen={isOpen} hasData loading={false} handleClose={onClose}>
        <DrawerHeader handleClose={onClose}>Care Team Profile</DrawerHeader>

        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={methods.handleSubmit(onSubmitHandler)}
            width="100%"
            noValidate
          >
            <Stack spacing={1.5}>
              <FormInput
                label="First Name"
                name="firstName"
                fullWidth
                disabled={isRegisteredMember || isReadOnly}
              />
              <FormInput
                label="Last Name"
                name="lastName"
                fullWidth
                disabled={isRegisteredMember || isReadOnly}
              />
              <FormInput
                label="Email"
                name="email"
                fullWidth
                disabled={isRegisteredMember || isReadOnly}
              />
              <FormInput
                label="Mobile Phone Number"
                name="phone"
                fullWidth
                disabled={isRegisteredMember || isReadOnly}
              />

              <CaregiverRoleFields disabled={isRegisteredMember || isReadOnly} />

              <FormInput
                label="Street Address"
                name="addressStreet"
                fullWidth
                disabled={isReadOnly}
              />
              <FormInput label="Zip Code" name="addressZipCode" fullWidth disabled={isReadOnly} />
              <FormInput label="City" name="addressCity" fullWidth disabled={isReadOnly} />
              <FormInput label="State" name="addressState" fullWidth disabled={isReadOnly} />
              <FormInput label="Country" name="addressCountry" fullWidth disabled={isReadOnly} />

              <FormInput
                name="about"
                label="What you might want to know about me"
                placeholder="Please enter the relevant information about you"
                fullWidth
                minRows={3}
                maxRows={6}
                multiline
                helperText={charactersLeft}
                checkTouchField={false}
                disabled={isRegisteredMember || isReadOnly}
              />

              <FormInput
                label="Comments"
                name="comments"
                fullWidth
                multiline
                disabled={isReadOnly}
              />
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
              <Button variant="outlined" onClick={onClose} sx={minButtonWidth}>
                Close
              </Button>
              <LoadingButton
                loading={updateMutation.isLoading}
                variant="contained"
                type="submit"
                sx={minButtonWidth}
                disabled={isReadOnly}
              >
                Save
              </LoadingButton>
            </Stack>
          </Box>
        </FormProvider>
      </DrawerContainer>
    </Drawer>
  )
}
